import React, { useEffect, useState } from "react";

/**
 * Animated circular gauge (SVG).
 * @param {{ value: number, size?: number, stroke?: number, label?: string, color?: string }} props
 */
export const ScoreGauge = ({
  value = 0,
  size = 220,
  stroke = 14,
  label,
  color = "url(#gaugeGradient)",
  ariaLabel,
}) => {
  const [display, setDisplay] = useState(0);
  const clamped = Math.max(0, Math.min(100, value));

  useEffect(() => {
    const start = performance.now();
    const duration = 1200;
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * clamped));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [clamped]);

  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (display / 100) * c;

  return (
    <div
      className="relative inline-flex items-center justify-center"
      style={{ width: size, height: size }}
      aria-label={ariaLabel || `Score ${clamped}/100`}
      data-testid="score-gauge"
    >
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="gaugeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#4ca6ff" />
            <stop offset="100%" stopColor="#0a1b3d" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          className="gauge-track"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={offset}
          className="gauge-fill"
          style={{ stroke: color }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-display font-black tabular-nums" style={{ fontSize: size * 0.28 }}>
          {display}
        </div>
        <div className="eyebrow mt-1">/ 100</div>
        {label && (
          <div className="text-xs text-[color:var(--impact-ink-2)] mt-2 text-center max-w-[80%]">
            {label}
          </div>
        )}
      </div>
    </div>
  );
};
